import {
    Box,
    Stack,
    Typography,
    IconButton,
    Chip,
    Skeleton,
    Button,
} from "@compass/core";
import { Dialog, DialogContent, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { NotificationDto } from "generated/openApi";
import { AppTopBarNotificationsCard } from "./AppTopBarNotificationsCard";
import { pillToggleGroupStyles } from "pages/HomePage/styles/segmentedControlStyles";
import {
    APP_TOP_BAR_NOTIFICATIONS_COPY,
    APP_TOP_BAR_NOTIFICATIONS_DIALOG_TITLE_ID,
    APP_TOP_BAR_NOTIFICATIONS_EMPTY_STATE_COPY,
    APP_TOP_BAR_NOTIFICATIONS_ERROR_STATE_COPY,
    APP_TOP_BAR_NOTIFICATIONS_RETRY_LABEL,
    AppTopBarNotificationsTab,
    NOTIFICATIONS_DIALOG_MAX_HEIGHT,
    NOTIFICATIONS_FIRST_PAGE,
    NOTIFICATIONS_HEADER_ACCENT_WIDTH_PX,
    NOTIFICATIONS_SKELETON_WIDTHS,
    NOTIFICATIONS_TIMELINE_DOT_BORDER_WIDTH_PX,
    NOTIFICATIONS_TIMELINE_GEOMETRY,
    PREVIOUS_NOTIFICATIONS_PAGE_SIZE,
} from "./AppTopBarNotificationsConstants";

interface AppTopBarNotificationsModalProps {
    open: boolean;
    activeTab: AppTopBarNotificationsTab;
    newNotifications: NotificationDto[];
    newNotificationsLoading: boolean;
    newNotificationsError: boolean;
    previousNotifications: NotificationDto[];
    previousNotificationsLoading: boolean;
    previousNotificationsPaging: boolean;
    previousNotificationsError: boolean;
    previousPage: number;
    previousTotalCount: number;
    unreadCount: number;
    onToggleInteraction: () => void;
    onTabChange: (tab: AppTopBarNotificationsTab) => void;
    onPreviousPageChange: (page: number) => void;
    onRetry: () => void;
    onClose: () => void;
}

export const AppTopBarNotificationsModal = ({
    open,
    activeTab,
    newNotifications,
    newNotificationsLoading,
    newNotificationsError,
    previousNotifications,
    previousNotificationsLoading,
    previousNotificationsPaging,
    previousNotificationsError,
    previousPage,
    previousTotalCount,
    unreadCount,
    onToggleInteraction,
    onTabChange,
    onPreviousPageChange,
    onRetry,
    onClose,
}: AppTopBarNotificationsModalProps) => {
    const theme = useTheme();
    const isNewTab = activeTab === AppTopBarNotificationsTab.New;
    const notifications = isNewTab ? newNotifications : previousNotifications;
    const loading = isNewTab ? newNotificationsLoading : previousNotificationsLoading;
    const error = isNewTab ? newNotificationsError : previousNotificationsError;
    const emptyStateCopy = APP_TOP_BAR_NOTIFICATIONS_EMPTY_STATE_COPY[activeTab];
    const errorStateCopy = APP_TOP_BAR_NOTIFICATIONS_ERROR_STATE_COPY[activeTab];

    const handleTabChange = (
        _event: React.MouseEvent<HTMLElement>,
        tab: AppTopBarNotificationsTab | null
    ) => {
        onToggleInteraction();
        if (!tab || tab === activeTab) return;

        onTabChange(tab);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby={APP_TOP_BAR_NOTIFICATIONS_DIALOG_TITLE_ID}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    maxHeight: NOTIFICATIONS_DIALOG_MAX_HEIGHT,
                    bgcolor: theme.palette.background.default,
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    mx: 3,
                    mt: 2,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 3,
                    p: 3,
                    boxShadow: theme.shadows[2],
                    border: 1,
                    borderColor: theme.palette.divider,
                    borderLeftWidth: NOTIFICATIONS_HEADER_ACCENT_WIDTH_PX,
                    borderLeftColor: theme.palette.primary.main,
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing="sm">
                        <Typography
                            id={APP_TOP_BAR_NOTIFICATIONS_DIALOG_TITLE_ID}
                            variant="h4"
                            sx={{
                                fontWeight: theme.typography.fontWeightBold,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {APP_TOP_BAR_NOTIFICATIONS_COPY.dialogTitle}
                        </Typography>
                        {isNewTab && unreadCount > 0 && (
                            <Chip
                                label={`${unreadCount} ${APP_TOP_BAR_NOTIFICATIONS_COPY.unreadLabel}`}
                                size="small"
                                color="error"
                            />
                        )}
                    </Stack>

                    <IconButton
                        onClick={onClose}
                        size="small"
                        aria-label={APP_TOP_BAR_NOTIFICATIONS_COPY.closeButtonLabel}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </Box>

            <Box sx={{ mx: 3, mt: 2 }}>
                <ToggleButtonGroup
                    value={activeTab}
                    exclusive
                    onChange={handleTabChange}
                    aria-label={APP_TOP_BAR_NOTIFICATIONS_COPY.toggleGroupLabel}
                    size="small"
                    sx={pillToggleGroupStyles}
                >
                    <ToggleButton value={AppTopBarNotificationsTab.New}>
                        {APP_TOP_BAR_NOTIFICATIONS_COPY.newTabLabel}
                    </ToggleButton>
                    <ToggleButton value={AppTopBarNotificationsTab.Previous}>
                        {APP_TOP_BAR_NOTIFICATIONS_COPY.previousTabLabel}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Content */}
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 2 }}>
                    {loading ? (
                        <NotificationsLoadingSkeleton theme={theme} />
                    ) : error ? (
                        <NotificationsErrorState
                            title={errorStateCopy.title}
                            message={errorStateCopy.message}
                            theme={theme}
                            onRetry={onRetry}
                        />
                    ) : notifications.length === 0 ? (
                        <NotificationsEmptyState
                            title={emptyStateCopy.title}
                            message={emptyStateCopy.message}
                            theme={theme}
                        />
                    ) : (
                        <Box
                            aria-busy={!isNewTab && previousNotificationsPaging}
                            sx={{
                                position: "relative",
                                opacity: !isNewTab && previousNotificationsPaging
                                    ? theme.palette.action.disabledOpacity
                                    : undefined,
                                pointerEvents: !isNewTab && previousNotificationsPaging
                                    ? "none"
                                    : "auto",
                            }}
                        >
                            {/* Continuous timeline line */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: NOTIFICATIONS_TIMELINE_GEOMETRY.lineLeftPx,
                                    top: 0,
                                    bottom: 0,
                                    width: NOTIFICATIONS_TIMELINE_GEOMETRY.lineWidthPx,
                                    bgcolor: theme.palette.divider,
                                    zIndex: 0,
                                }}
                            />

                            {notifications.map((notification) => (
                                <AppTopBarNotificationsCard
                                    key={notification.id}
                                    notification={notification}
                                    theme={theme}
                                />
                            ))}
                        </Box>
                    )}

                    {!isNewTab && !error && previousTotalCount > 0 && (
                        <NotificationsPager
                            loading={
                                previousNotificationsLoading || previousNotificationsPaging
                            }
                            page={previousPage}
                            totalCount={previousTotalCount}
                            onPageChange={onPreviousPageChange}
                            theme={theme}
                        />
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

interface NotificationsLoadingSkeletonProps {
    theme: Theme;
}

const NotificationsLoadingSkeleton = ({ theme }: NotificationsLoadingSkeletonProps) => {

    return (
        <Box sx={{ position: "relative" }}>
            <Box
                sx={{
                    position: "absolute",
                    left: NOTIFICATIONS_TIMELINE_GEOMETRY.lineLeftPx,
                    top: 0,
                    bottom: 0,
                    width: NOTIFICATIONS_TIMELINE_GEOMETRY.lineWidthPx,
                    bgcolor: theme.palette.divider,
                    zIndex: 0,
                }}
            />
            {Array.from({ length: 4 }).map((_, index) => (
                <Box key={index} sx={{ position: "relative", pb: 4, pl: 6 }}>
                    <Box
                        sx={{
                            position: "absolute",
                            left: NOTIFICATIONS_TIMELINE_GEOMETRY.dotLeftPx,
                            top: NOTIFICATIONS_TIMELINE_GEOMETRY.dotTopPx,
                            width: NOTIFICATIONS_TIMELINE_GEOMETRY.dotDiameterPx,
                            height: NOTIFICATIONS_TIMELINE_GEOMETRY.dotDiameterPx,
                            borderRadius: NOTIFICATIONS_TIMELINE_GEOMETRY.dotBorderRadius,
                            bgcolor: theme.palette.text.secondary,
                            borderStyle: "solid",
                            borderWidth: NOTIFICATIONS_TIMELINE_DOT_BORDER_WIDTH_PX,
                            borderColor: theme.palette.background.paper,
                            zIndex: 1,
                        }}
                    />
                    <Skeleton
                        variant="text"
                        width={NOTIFICATIONS_SKELETON_WIDTHS.date}
                        sx={{ fontSize: theme.typography.h6.fontSize }}
                    />
                    <Box
                        sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: theme.shape.borderRadius * 2,
                            border: 1,
                            borderColor: theme.palette.divider,
                        }}
                    >
                        <Skeleton
                            variant="text"
                            width={NOTIFICATIONS_SKELETON_WIDTHS.title}
                            sx={{ fontSize: theme.typography.h6.fontSize }}
                        />
                        <Skeleton
                            variant="text"
                            width={NOTIFICATIONS_SKELETON_WIDTHS.firstBodyLine}
                            sx={{ mt: 1, fontSize: theme.typography.body2.fontSize }}
                        />
                        <Skeleton
                            variant="text"
                            width={NOTIFICATIONS_SKELETON_WIDTHS.secondBodyLine}
                            sx={{ fontSize: theme.typography.body2.fontSize }}
                        />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

interface NotificationsEmptyStateProps {
    title: string;
    message: string;
    theme: Theme;
}

const NotificationsEmptyState = ({ title, message, theme }: NotificationsEmptyStateProps) => {

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
            }}
        >
            <NotificationsNoneOutlinedIcon
                sx={{
                    fontSize: theme.typography.h3.fontSize,
                    color: theme.palette.text.secondary,
                    mb: 2,
                }}
            />
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                {title}
            </Typography>
            <Typography
                variant="body2"
                sx={{ mt: 0.5, color: theme.palette.text.secondary }}
            >
                {message}
            </Typography>
        </Box>
    );
};

interface NotificationsErrorStateProps {
    title: string;
    message: string;
    theme: Theme;
    onRetry: () => void;
}

const NotificationsErrorState = ({
    title,
    message,
    theme,
    onRetry,
}: NotificationsErrorStateProps) => (
    <Box
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
        }}
    >
        <NotificationsNoneOutlinedIcon
            sx={{
                fontSize: theme.typography.h3.fontSize,
                color: theme.palette.text.secondary,
                mb: 2,
            }}
        />
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
            {title}
        </Typography>
        <Typography
            variant="body2"
            sx={{ mt: 0.5, color: theme.palette.text.secondary }}
        >
            {message}
        </Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={onRetry}>
            {APP_TOP_BAR_NOTIFICATIONS_RETRY_LABEL}
        </Button>
    </Box>
);

interface NotificationsPagerProps {
    loading: boolean;
    page: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    theme: Theme;
}

const NotificationsPager = ({
    loading,
    page,
    totalCount,
    onPageChange,
    theme,
}: NotificationsPagerProps) => {
    const totalPages = Math.ceil(totalCount / PREVIOUS_NOTIFICATIONS_PAGE_SIZE);
    const hasPreviousPage = page > NOTIFICATIONS_FIRST_PAGE;
    const hasNextPage = page * PREVIOUS_NOTIFICATIONS_PAGE_SIZE < totalCount;

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
                mt: 2,
                pt: 2,
                borderTop: 1,
                borderColor: theme.palette.divider,
            }}
        >
            <Button
                size="small"
                variant="outlined"
                disabled={loading || !hasPreviousPage}
                onClick={() => onPageChange(page - 1)}
            >
                {APP_TOP_BAR_NOTIFICATIONS_COPY.previousTabLabel}
            </Button>
            <Typography variant="body2">
                {APP_TOP_BAR_NOTIFICATIONS_COPY.pageLabel} {page}{" "}
                {APP_TOP_BAR_NOTIFICATIONS_COPY.pageCountSeparator} {totalPages}
            </Typography>
            <Button
                size="small"
                variant="outlined"
                disabled={loading || !hasNextPage}
                onClick={() => onPageChange(page + 1)}
            >
                {APP_TOP_BAR_NOTIFICATIONS_COPY.nextPageLabel}
            </Button>
        </Box>
    );
};
